<script lang="ts">
	import classNames from 'classnames';
	import type { FormMarkdownTokens } from '../types';
	import { TickIcon } from '$global/assets/styled-icons/TickIcon';
	import { Checkbox } from '$global/components/Checkbox';
	import { Dropzone } from '$global/components/Dropzone';
	import { Input } from '$global/components/Input';
	import { InputLabel } from '$global/components/InputLabel';
	import { Markdown } from '$global/components/Markdown';
	import { Radio } from '$global/components/Radio';
	import type { RadioValue } from '$global/components/Radio/types';
	import type {
		SelectOption,
		SelectChangeEvent,
	} from '$global/components/Select';
	import { Select } from '$global/components/Select';
	import { Txt } from '$global/components/Txt';
	import { VideoPlayer } from '$global/components/VideoPlayer';
	import { getImageUrl } from '$global/utils/getImageUrl';
	import { validateEmail } from '$global/utils/validateEmail/validateEmail';
	import type {
		Form_Checkbox,
		Form_Datepicker,
		Form_Dropdown,
		Form_Group,
		Form_Image,
		Form_Input,
		FormSuccess,
		Form_Radio,
		Form_Text,
		Form_Text_Area,
		Form_Upload,
		Form_Video,
		Form_Page_Form_Components,
	} from '$gql/types';

	type FormValue = string | number | boolean | File | File[] | null;

	interface Props {
		index: number;
		component: Form_Page_Form_Components;
		dataCy?: string;
		formValues: Record<string, FormValue>;
		formBlurs: Record<string, boolean>;
		updateFormValue: (fieldName: string, value: FormValue) => void;
		parentGroupName?: string;
	}

	let {
		index,
		dataCy,
		component,
		formValues,
		formBlurs = $bindable(),
		updateFormValue,
		parentGroupName,
	}: Props = $props();

	let dataCyPrefix = $derived(`${dataCy}-component`);

	const getNestedStringValue = (fieldPath: string): string => {
		const parts = fieldPath.split('.');
		const value = parts.reduce<Record<string, FormValue> | FormValue>(
			(acc, part) => {
				if (
					acc &&
					typeof acc === 'object' &&
					!Array.isArray(acc) &&
					!(acc instanceof File)
				) {
					const result = (acc as Record<string, FormValue>)[part];
					return result !== undefined ? result : null;
				}
				return null;
			},
			formValues
		);

		return value === null ? '' : String(value || '');
	};

	const getCheckBoxValues = (fieldPath: string): boolean => {
		const parts = fieldPath.split('.');
		const value = parts.reduce<Record<string, FormValue> | FormValue>(
			(acc, part) => {
				if (
					acc &&
					typeof acc === 'object' &&
					!Array.isArray(acc) &&
					!(acc instanceof File)
				) {
					const result = (acc as Record<string, FormValue>)[part];
					return result !== undefined ? result : null;
				}
				return null;
			},
			formValues
		);

		return Boolean(value);
	};

	const getFieldPath = (fieldName: string) => {
		return parentGroupName ? `${parentGroupName}.${fieldName}` : fieldName;
	};

	const handleBlur = (fieldName: string) => {
		const fieldPath = getFieldPath(fieldName);
		formBlurs = {
			...formBlurs,
			[fieldPath]: true,
		};
	};

	const getBlurValue = (fieldName: string) => {
		return formBlurs[getFieldPath(fieldName)];
	};

	const handleInputChange = (fieldName: string, event: Event) => {
		const target = event.target as HTMLInputElement;
		const fieldPath = getFieldPath(fieldName);
		updateFormValue(fieldPath, target.value);
	};

	const handleCheckboxChange = (fieldName: string, value: string | boolean) => {
		const fieldPath = getFieldPath(fieldName);
		updateFormValue(fieldPath, Boolean(value));
	};

	const handleRadioChange = (fieldName: string, value: RadioValue) => {
		const fieldPath = getFieldPath(fieldName);
		updateFormValue(fieldPath, String(value));
	};

	const handleSelectChange = (fieldName: string, event: SelectChangeEvent) => {
		const value = event.detail.selectedOption.value || event.detail.value || '';
		const fieldPath = getFieldPath(fieldName);
		updateFormValue(fieldPath, String(value));
	};

	const handleFileUpload = (fieldName: string, files: File[]) => {
		const fieldPath = getFieldPath(fieldName);
		updateFormValue(fieldPath, files[0]);
	};
</script>

{#snippet renderComponent(
	comp: Form_Page_Form_Components,
	parentGroup?: string
)}
	{#if comp?.collection === 'Form_Video'}
		{@const videoItem = comp?.item as Form_Video}
		<VideoPlayer
			controls
			src={videoItem.video_url as string}
			title="form video"
			dataCy={`${dataCyPrefix}-video`}
			class={classNames('mb-6 pb-[56.25%]', { 'mx-4': index })}
			autoplay={false}
		/>
	{:else if comp?.collection === 'Form_Image'}
		{@const imageItem = comp?.item as Form_Image}
		<img
			src={getImageUrl(imageItem.image?.filename_disk)}
			alt={imageItem?.image?.title || 'form image'}
			class={classNames('mb-6 w-full object-cover', { 'px-4': !!index })}
			data-cy={`${dataCyPrefix}-image`}
		/>
	{:else if comp?.collection === 'FormSuccess'}
		{@const textItem = comp?.item as FormSuccess & FormMarkdownTokens}
		<div class="mb-4 flex justify-center">
			<TickIcon class="h-[5rem] w-[5rem]" />
		</div>
		<Markdown
			dataCy="drop-timeline-entry-content"
			class={classNames(
				'mb-6 px-4 leading-[1.25rem] [&_h1]:mb-4 [&_h1]:text-[1.5rem] [&_h1]:leading-[2rem] [&_h1]:tracking-[0] [&_p]:mb-4'
			)}
			source={textItem.textTokens ?? []}
			size="medium"
		/>
	{:else if comp?.collection === 'Form_Text'}
		{@const textItem = comp?.item as Form_Text & FormMarkdownTokens}
		<Markdown
			dataCy="drop-timeline-entry-content"
			class={classNames(
				'mb-6 px-4 leading-[1.25rem] [&_h1]:mb-4 [&_h1]:text-[1.5rem] [&_h1]:leading-[2rem] [&_h1]:tracking-[0] [&_p]:mb-4',
				{ 'mt-6': !index }
			)}
			source={textItem.textTokens ?? []}
			size="medium"
		/>
	{:else if comp?.collection === 'Form_Input'}
		{@const inputItem = comp?.item as Form_Input}
		{#if !inputItem.hidden && inputItem.field_name}
			{@const fieldPath = parentGroup
				? `${parentGroup}.${inputItem.field_name}`
				: inputItem.field_name}

			<Input
				dataCy={`${dataCyPrefix}-input`}
				id={inputItem.id}
				placeholder={inputItem.placeholder as string}
				name={inputItem.field_name}
				label={inputItem.label as string}
				type="text"
				onblur={() => {
					handleBlur(inputItem.field_name as string);
				}}
				required={inputItem.required || false}
				classes={{
					error: 'text-left px-4',
					wrapper: 'px-4',
					labelWrapper: 'px-4',
					helper: 'mt-1 mb-6 text-left px-4',
				}}
				error={(() => {
					if (!getBlurValue(inputItem.field_name)) return '';

					const value = getNestedStringValue(fieldPath);

					if (inputItem.required && !value) {
						return 'This field is required';
					}

					if (
						value &&
						inputItem.field_name.includes('email') &&
						!validateEmail(value)
					) {
						return 'This email address is not valid';
					}

					return '';
				})()}
				value={getNestedStringValue(fieldPath)}
				oninput={(event) =>
					handleInputChange(inputItem.field_name as string, event)}
			>
				{#snippet helper()}
					{inputItem.help_text}
				{/snippet}
			</Input>
		{/if}
	{:else if comp?.collection === 'Form_Text_Area'}
		{@const textAreaItem = comp?.item as Form_Text_Area}
		{#if textAreaItem.field_name}
			{@const fieldPath = parentGroup
				? `${parentGroup}.${textAreaItem.field_name}`
				: textAreaItem.field_name}
			{@const fieldName = textAreaItem.field_name as string}

			<Input
				dataCy={`${dataCyPrefix}-textarea`}
				id={textAreaItem.id}
				placeholder={textAreaItem.placeholder as string}
				name={textAreaItem.field_name}
				label={textAreaItem.label as string}
				rows={textAreaItem.number_of_lines || 4}
				type="text"
				required={textAreaItem.required || false}
				classes={{
					wrapper: 'px-4',
					labelWrapper: 'px-4 text-left',
					helper: 'mt-1 mb-6 text-left px-4',
				}}
				value={getNestedStringValue(fieldPath)}
				oninput={(event) => handleInputChange(fieldName, event)}
			>
				{#snippet helper()}
					{textAreaItem.help_text}
				{/snippet}
			</Input>
		{/if}
	{:else if comp?.collection === 'Form_Radio' && (comp?.item as Form_Radio).field_name}
		{@const radioItem = comp?.item as Form_Radio}
		{@const fieldName = radioItem.field_name as string}

		<div class="mb-6 px-4">
			<Txt variant="label3" class="mb-3 text-left">
				{radioItem?.label}
				{#if radioItem.required}
					<span class="text-red-500">*</span>
				{/if}
			</Txt>

			<div class="space-y-2">
				{#each radioItem.radio_items || [] as radio}
					{#if radio?.Form_Radio_Item_id}
						{@const radioValue =
							radio.Form_Radio_Item_id.value ||
							radio.Form_Radio_Item_id.label ||
							''}
						<InputLabel
							dataCy={`${dataCyPrefix}-radio-option`}
							class="flex w-full items-center px-2 py-1"
						>
							<Radio
								size="md"
								dataCy={`${dataCyPrefix}-radio-input`}
								name={fieldName}
								value={radioValue}
								onChange={(value) => handleRadioChange(fieldName, value)}
							/>
							<span class="ml-2">{radio.Form_Radio_Item_id.label}</span>
						</InputLabel>
					{/if}
				{/each}
			</div>

			{#if (comp?.item as Form_Radio).help_text}
				<Txt variant="body3" class="mt-2 text-left text-gray-500">
					{(comp?.item as Form_Radio).help_text}
				</Txt>
			{/if}
		</div>
	{:else if comp?.collection === 'Form_Dropdown' && (comp?.item as Form_Dropdown).field_name}
		{@const dropdownItem = comp?.item as Form_Dropdown}
		{@const fieldPath = parentGroup
			? `${parentGroup}.${dropdownItem.field_name}`
			: dropdownItem.field_name}
		{@const fieldName = dropdownItem.field_name as string}

		<div class="mb-6 px-4">
			<Select
				label={dropdownItem?.label as string}
				required={dropdownItem.required as boolean}
				value={getNestedStringValue(fieldPath as string)}
				dataCy={`${dataCyPrefix}-dropdown`}
				name={fieldName}
				placeholder={dropdownItem.placeholder as string}
				ariaLabel="drop down"
				options={(dropdownItem?.dropdown_items?.map((item) => ({
					value: item?.Form_Dropdown_Item_id?.value || '',
					label: item?.Form_Dropdown_Item_id?.label || '',
				})) as SelectOption[]) || []}
				onchange={(event) => handleSelectChange(fieldName, event)}
				classes={{
					helper: 'text-left font-normal',
					label: 'text-left',
				}}
			>
				{dropdownItem?.help_text}
			</Select>
		</div>
	{:else if comp?.collection === 'Form_Datepicker'}
		{@const datepickerItem = comp?.item as Form_Datepicker}
		{#if datepickerItem?.field_name}
			{@const fieldPath = parentGroup
				? `${parentGroup}.${datepickerItem.field_name}`
				: datepickerItem.field_name}
			{@const fieldName = datepickerItem.field_name as string}

			<Input
				name={datepickerItem.field_name}
				label={datepickerItem?.label as string}
				placeholder="Pick a date"
				dataCy={`${dataCyPrefix}-date`}
				type="date"
				required={datepickerItem?.required || false}
				classes={{
					wrapper: 'px-4',
					labelWrapper: 'px-4',
					helper: 'mt-1 mb-6 px-4 text-left',
				}}
				value={getNestedStringValue(fieldPath)}
				oninput={(event) => handleInputChange(fieldName, event)}
			>
				{#snippet helper()}
					{datepickerItem?.help_text}
				{/snippet}
			</Input>
		{/if}
	{:else if comp?.collection === 'Form_Upload'}
		{@const uploadItem = comp?.item as Form_Upload}
		{@const fieldName = uploadItem?.field_name as string}

		<div class="mb-6 px-4">
			{#if uploadItem?.label}
				<Txt variant="label3" class="mb-3 text-left">
					{uploadItem.label}
					{#if uploadItem.required}
						<span class="text-red-500">*</span>
					{/if}
				</Txt>
			{/if}

			<Dropzone
				multiple={false}
				dataCy={dataCyPrefix}
				accept={['image/png', 'image/jpeg', 'application/pdf']}
				onSubmitFiles={(files) => {
					handleFileUpload(fieldName, files);
				}}
			>
				{#snippet helper()}
					<Txt variant="body3" class="mt-1 text-left text-gray-500">
						{uploadItem?.help_text}. Max file size is 20MB.
					</Txt>
				{/snippet}
			</Dropzone>
		</div>
	{:else if comp?.collection === 'Form_Checkbox'}
		{@const checkboxItem = comp?.item as Form_Checkbox & FormMarkdownTokens}
		{@const fieldName = checkboxItem?.field_name as string}
		{@const fieldPath = parentGroup ? `${parentGroup}.${fieldName}` : fieldName}

		<InputLabel
			dataCy={`${dataCyPrefix}-checkbox-label`}
			variant="body3"
			class="mb-6 flex flex-row items-start justify-start px-4"
		>
			<Checkbox
				dataCy={`${dataCyPrefix}-checkbox`}
				name={fieldName}
				id={checkboxItem?.id}
				class="mt-1 mr-3"
				checked={getCheckBoxValues(fieldPath)}
				onChange={(value) => handleCheckboxChange(fieldName, value)}
			/>
			<div class="flex-1">
				<Markdown
					dataCy={`${dataCyPrefix}-checkbox-text`}
					source={checkboxItem?.labelTokens ?? []}
					class="inline-block text-left font-[500] [&>p]:m-0"
					size="medium"
				/>
				{#if checkboxItem.required}
					<span class="ml-[-3px] inline text-red-500">*</span>
				{/if}

				<Txt variant="body3" class="mt-1 block text-left text-gray-500"
					>{checkboxItem?.help_text}</Txt
				>
			</div>
		</InputLabel>
	{:else if comp?.collection === 'Form_Group'}
		{@const formGroupItem = comp?.item as Form_Group}
		{@const groupName = formGroupItem?.name || `group_${formGroupItem?.id}`}
		{@const newParentGroup = parentGroup
			? `${parentGroup}.${groupName}`
			: groupName}
		<div class="form-group mb-6">
			{#each formGroupItem.form_group_components || [] as groupComponent}
				{@render renderComponent(
					groupComponent as Form_Page_Form_Components,
					newParentGroup
				)}
			{/each}
		</div>
	{/if}
{/snippet}

{@render renderComponent(component, parentGroupName)}
